

import { useState, useEffect, useMemo } from "react";
import { 
  Plus, 
  Search, 
  LayoutGrid, 
  Table2, 
  Pencil, 
  Trash2, 
  Calendar,
  Clock,
  User,
  Phone,
  Mail,
  Building2,
  CheckCircle,
  XCircle,
  AlertCircle
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Badge from "../components/Badge";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { bookingsApi, mastersApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { formatCurrency, formatDate } from "@/lib/data";

// Types
interface Booking {
  id: number;
  customer_name: string;
  customer_phone?: string;
  customer_email?: string;
  room_id?: number;
  room?: { id: number; name: string; type: string };
  event_id?: number;
  event?: { id: number; name: string };
  venue_id?: number;
  venue?: { id: number; name: string };
  check_in: string;
  check_out: string;
  status: 'confirmed' | 'pending' | 'cancelled' | 'completed';
  rate?: number;
  notes?: string;
  created_at?: string;
}

interface Room {
  id: number;
  name: string;
  type: string;
  capacity: number;
  rate: number;
  status: string;
}

// View Toggle Component
function ViewToggle({ view, onChange }: { view: 'card' | 'table'; onChange: (v: 'card' | 'table') => void }) {
  return (
    <div className="flex bg-stone-100 rounded-xl p-1">
      <button
        onClick={() => onChange('card')}
        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
          view === 'card' ? 'bg-white text-stone-800 border border-stone-200' : 'text-stone-500 hover:text-stone-700'
        }`}
      >
        <LayoutGrid className="w-4 h-4" />
      </button>
      <button
        onClick={() => onChange('table')}
        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
          view === 'table' ? 'bg-white text-stone-800 border border-stone-200' : 'text-stone-500 hover:text-stone-700'
        }`}
      >
        <Table2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// Search Input Component
function SearchInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search bookings..."
        className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
      />
    </div>
  );
}

// Compact Stat Card
function StatCard({ label, value, icon: Icon, color = "amber" }: { label: string; value: string; icon: any; color?: "amber" | "emerald" | "blue" | "rose" }) {
  const colors = {
    amber: "bg-amber-50 text-amber-600",
    emerald: "bg-emerald-50 text-emerald-600",
    blue: "bg-blue-50 text-blue-600",
    rose: "bg-rose-50 text-rose-600",
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-3 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs text-stone-500">{label}</div>
        <div className="text-lg font-bold text-stone-800">{value}</div>
      </div>
    </div>
  );
}

// Action Button
function ActionButton({ 
  onClick, 
  icon: Icon, 
  label, 
  variant = 'default' 
}: { 
  onClick: () => void; 
  icon: any; 
  label: string;
  variant?: 'default' | 'primary' | 'danger' | 'success';
}) {
  const colors = {
    default: 'text-stone-600 hover:bg-stone-100',
    primary: 'text-amber-600 hover:bg-amber-50',
    danger: 'text-red-600 hover:bg-red-50',
    success: 'text-emerald-600 hover:bg-emerald-50',
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${colors[variant]}`}
      title={label}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// Status Badge
function StatusBadge({ status }: { status: string }) {
  const configs = {
    confirmed: { icon: CheckCircle, className: "bg-emerald-100 text-emerald-700" },
    pending: { icon: AlertCircle, className: "bg-amber-100 text-amber-700" },
    cancelled: { icon: XCircle, className: "bg-rose-100 text-rose-700" },
    completed: { icon: CheckCircle, className: "bg-blue-100 text-blue-700" },
  };
  const config = configs[status as keyof typeof configs] || configs.pending;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${config.className}`}>
      <Icon className="w-3 h-3" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

interface Venue {
  id: number;
  name: string;
}

// Booking Form Component
function BookingForm({ 
  initialData, 
  onSubmit, 
  onCancel,
  rooms,
  venues
}: { 
  initialData?: Partial<Booking>; 
  onSubmit: (data: any) => void;
  onCancel: () => void;
  rooms: Room[];
  venues: Venue[];
}) {
  const [formData, setFormData] = useState<{
    customer_name: string;
    customer_phone: string;
    customer_email: string;
    room_id: string;
    venue_id: string;
    check_in: string;
    check_out: string;
    status: string;
    rate: string;
    notes: string;
  }>({
    customer_name: initialData?.customer_name || "",
    customer_phone: initialData?.customer_phone || "",
    customer_email: initialData?.customer_email || "",
    room_id: initialData?.room_id ? String(initialData.room_id) : "",
    venue_id: initialData?.venue_id ? String(initialData.venue_id) : "",
    check_in: initialData?.check_in ? new Date(initialData.check_in).toISOString().slice(0, 16) : "",
    check_out: initialData?.check_out ? new Date(initialData.check_out).toISOString().slice(0, 16) : "",
    status: initialData?.status || "pending",
    rate: initialData?.rate ? String(initialData.rate) : "",
    notes: initialData?.notes || "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      customer_name: formData.customer_name,
      customer_phone: formData.customer_phone,
      customer_email: formData.customer_email,
      room_id: formData.room_id ? parseInt(formData.room_id) : null,
      venue_id: formData.venue_id ? parseInt(formData.venue_id) : null,
      check_in: formData.check_in,
      check_out: formData.check_out,
      status: formData.status,
      rate: formData.rate ? parseFloat(formData.rate) : null,
      notes: formData.notes,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Customer Name *</label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              placeholder="Enter customer name"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Phone</label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="tel"
              value={formData.customer_phone}
              onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
              placeholder="+91 98765 43210"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="email"
              value={formData.customer_email}
              onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
              placeholder="customer@email.com"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Venue</label>
          <select
            value={formData.venue_id}
            onChange={(e) => setFormData({ ...formData, venue_id: e.target.value })}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none appearance-none bg-white"
          >
            <option value="">Select a venue</option>
            {venues.map((venue) => (
              <option key={venue.id} value={venue.id}>
                {venue.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Room</label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <select
              value={formData.room_id}
              onChange={(e) => setFormData({ ...formData, room_id: e.target.value })}
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none appearance-none bg-white"
            >
              <option value="">Select a room</option>
              {rooms.map((room) => (
                <option key={room.id} value={room.id}>
                  {room.name} ({room.type}) - {formatCurrency(room.rate)}/night
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Check In *</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="datetime-local"
              value={formData.check_in}
              onChange={(e) => setFormData({ ...formData, check_in: e.target.value })}
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Check Out *</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="datetime-local"
              value={formData.check_out}
              onChange={(e) => setFormData({ ...formData, check_out: e.target.value })}
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
              required
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          >
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Rate (per night)</label>
          <input
            type="number"
            value={formData.rate}
            onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
            placeholder="0.00"
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Notes</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Additional notes..."
          rows={2}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData ? "Update" : "Add"} Booking
        </button>
      </div>
    </form>
  );
}

export default function BookingsPage() {
  const { showToast } = useToast();
  
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'card' | 'table'>('table');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  
  // Delete confirmation state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingBooking, setDeletingBooking] = useState<Booking | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [bookingsRes, roomsRes, venuesRes] = await Promise.all([
        bookingsApi.list(),
        mastersApi.rooms.list(),
        mastersApi.venues.list()
      ]);
      setBookings(bookingsRes.data?.data || bookingsRes.data || []);
      setRooms(roomsRes.data?.data || roomsRes.data || []);
      setVenues(venuesRes.data?.data || venuesRes.data || []);
    } catch (error) {
      showToast("Failed to load bookings", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredBookings = useMemo(() => {
    let filtered = bookings;
    
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(b => 
        b.customer_name.toLowerCase().includes(q) ||
        b.customer_phone?.toLowerCase().includes(q) ||
        b.customer_email?.toLowerCase().includes(q) ||
        b.room?.name.toLowerCase().includes(q)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }
    
    return filtered;
  }, [bookings, searchQuery, statusFilter]);

  const stats = useMemo(() => ({
    total: bookings.length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    pending: bookings.filter(b => b.status === 'pending').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
    totalRevenue: bookings
      .filter(b => b.status === 'confirmed' || b.status === 'completed')
      .reduce((sum, b) => sum + (b.rate || 0), 0),
  }), [bookings]);

  const openDeleteModal = (booking: Booking) => {
    setDeletingBooking(booking);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingBooking) return;
    
    setDeleteLoading(true);
    const previousBookings = bookings;
    setBookings(prev => prev.filter(b => b.id !== deletingBooking.id));
    
    try {
      await bookingsApi.delete(deletingBooking.id);
      showToast("Booking deleted successfully", "success");
      setDeleteModalOpen(false);
      setDeletingBooking(null);
    } catch (error) {
      setBookings(previousBookings);
      showToast("Failed to delete booking", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSubmit = async (data: any) => {
    setIsModalOpen(false);
    setEditingBooking(null);
    
    try {
      if (editingBooking) {
        const updated = { ...editingBooking, ...data };
        setBookings(prev => prev.map(b => b.id === editingBooking.id ? updated as Booking : b));
        await bookingsApi.update(editingBooking.id, data);
        showToast("Booking updated successfully", "success");
      } else {
        const tempId = Date.now();
        const optimisticBooking = {
          id: tempId,
          ...data,
          room: rooms.find(r => r.id === parseInt(data.room_id)),
        } as Booking;
        setBookings(prev => [optimisticBooking, ...prev]);
        
        const response = await bookingsApi.create(data);
        const newBooking = response.data?.data || response.data;
        
        setBookings(prev => prev.map(b => b.id === tempId ? newBooking : b));
        showToast("Booking created successfully", "success");
      }
    } catch (error) {
      showToast("Failed to save booking", "error");
      loadData();
    }
  };

  const getDuration = (checkIn: string, checkOut: string) => {
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const nights = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return nights > 0 ? `${nights} night${nights > 1 ? 's' : ''}` : 'Same day';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-800 mb-1">Bookings</h1>
        <p className="text-sm text-stone-500">Manage room bookings and reservations</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total" value={String(stats.total)} icon={Calendar} color="amber" />
        <StatCard label="Confirmed" value={String(stats.confirmed)} icon={CheckCircle} color="emerald" />
        <StatCard label="Pending" value={String(stats.pending)} icon={AlertCircle} color="blue" />
        <StatCard label="Cancelled" value={String(stats.cancelled)} icon={XCircle} color="rose" />
        <StatCard label="Revenue" value={formatCurrency(stats.totalRevenue)} icon={Building2} color="emerald" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1">
          <SearchInput value={searchQuery} onChange={setSearchQuery} />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none bg-white"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <ViewToggle view={viewMode} onChange={setViewMode} />
          <button
            onClick={() => { setEditingBooking(null); setIsModalOpen(true); }}
            className="px-4 py-2 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Booking
          </button>
        </div>
      </div>

      {/* Content */}
      {filteredBookings.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No bookings found</p>
        </div>
      ) : viewMode === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredBookings.map((booking) => (
            <div key={booking.id} className="bg-white rounded-xl border border-stone-200 overflow-hidden">
              <div className="p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-xl font-bold text-amber-600">
                      {booking.customer_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-stone-800 truncate">{booking.customer_name}</h3>
                      <StatusBadge status={booking.status} />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm mb-4">
                  {booking.room && (
                    <div className="flex items-center gap-2 text-stone-600">
                      <Building2 className="w-4 h-4 text-stone-400" />
                      <span>{booking.room.name}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-stone-600">
                    <Clock className="w-4 h-4 text-stone-400" />
                    <span>{getDuration(booking.check_in, booking.check_out)}</span>
                  </div>
                  <div className="text-xs text-stone-400">
                    {formatDate(booking.check_in)} - {formatDate(booking.check_out)}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                  <span className="font-semibold text-stone-800">
                    {booking.rate ? formatCurrency(booking.rate) : '-'}
                  </span>
                  <div className="flex flex-wrap gap-1">
                    <ActionButton 
                      onClick={() => { setEditingBooking(booking); setIsModalOpen(true); }} 
                      icon={Pencil}
                      label="Edit"
                      variant="default"
                    />
                    <ActionButton 
                      onClick={() => openDeleteModal(booking)} 
                      icon={Trash2}
                      label="Delete" 
                      variant="danger"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Customer</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Room</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Dates</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Rate</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center text-sm font-bold text-amber-600">
                          {booking.customer_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <span className="font-medium text-stone-800">{booking.customer_name}</span>
                          {booking.customer_phone && (
                            <p className="text-xs text-stone-400">{booking.customer_phone}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-stone-600">
                      {booking.room ? booking.room.name : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="text-stone-600">{formatDate(booking.check_in)}</div>
                      <div className="text-xs text-stone-400">
                        {getDuration(booking.check_in, booking.check_out)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={booking.status} />
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium">
                      {booking.rate ? formatCurrency(booking.rate) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <ActionButton onClick={() => { setEditingBooking(booking); setIsModalOpen(true); }} icon={Pencil} label="" variant="default" />
                        <ActionButton onClick={() => openDeleteModal(booking)} icon={Trash2} label="" variant="danger" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingBooking(null); }}
        title={editingBooking ? "Edit Booking" : "Add New Booking"}
        size="md"
      >
        <BookingForm
          initialData={editingBooking || undefined}
          onSubmit={handleFormSubmit}
          onCancel={() => { setIsModalOpen(false); setEditingBooking(null); }}
          rooms={rooms}
          venues={venues}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeletingBooking(null); }}
        onConfirm={handleDelete}
        title="Delete Booking"
        message="Are you sure you want to delete this booking? This action cannot be undone."
        itemName={deletingBooking?.customer_name}
        loading={deleteLoading}
      />
    </DashboardLayout>
  );
}
